import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStore } from "../store/globalState";
import { useTranslation } from "react-i18next";
import { usePreventReload } from "lola-framework-ui-test/src/hooks";
import {
  AuraLayout,
  Button,
  DebitCardIcon,
  Layout,
} from "lola-framework-ui-test";
import { IMaskInput } from "react-imask";
import IMask from "imask";
import { APP_ROUTES, flavorClassName, isFlavorB, TITLE_COLOR } from "../constants";
import { FloatingField } from "../components/FloatingField";
import {
  CARD_BRANDS,
  detectCardBrand,
  DEFAULT_ALLOWED_BRANDS,
  type CardBrandKey,
} from "../constants/card-brands";
import { useSessionStore } from "../store/useSessionStore";
import "../styles/cardpage.style.css";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useVirtualKeyboardOpen } from "../hooks/useVirtualKeyboardOpen";
import { useSurfaceAccent } from "../hooks/useSurfaceAccent";
import { getEvents, safeCapture } from "../analytics";
import { blurActiveTextInput } from "../utils/blurActiveTextInput";
import {
  CARDHOLDER_NAME_INPUT_MAX_LENGTH,
  formatCardholderNameInput,
  isValidCardholderName,
} from "../utils/cardholderName";
import { Navbar } from "../components/Navbar";
import { CloseLight } from "../assets/navIcons";

export interface CardStepFields {
  cardNumber: string;
  expiry: string;
  cardHolderName: string;
  cvv: string;
}

interface CardFormErrors {
  cardNumber?: string;
  expiry?: string;
  cardHolderName?: string;
  cvv?: string;
}

export const AddCardPage = memo(() => {
  useClaimLink();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const leaveCardForm = () =>
    redirectTo(APP_ROUTES.TRANSFER_PAGE, returnTo ? { replace: true } : undefined);
  const { t } = useTranslation();
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;

  useEffect(() => {
    safeCapture(getEvents().ADD_CARD_VIEW);
  }, []);

  const allowedBrands = (session.data?.config?.cardPolicy?.allowedCardBrands ??
    DEFAULT_ALLOWED_BRANDS) as CardBrandKey[];
  const isUSOnly =
    (session.data?.config?.cardPolicy?.cardOriginPolicy ?? "us_only") ===
    "us_only";

  const [form, setForm] = useState<CardStepFields>({
    cardNumber: "",
    expiry: "",
    cardHolderName: "",
    cvv: "",
  });
  const [errors, setErrors] = useState<CardFormErrors>({});

  const detectedBrand = useMemo(
    () => detectCardBrand(form.cardNumber),
    [form.cardNumber]
  );

  const detectedBrandInfo =
    detectedBrand && allowedBrands.includes(detectedBrand)
      ? CARD_BRANDS[detectedBrand]
      : null;

  const formatList = useCallback(() => {
    const formatter = new Intl.ListFormat(
      session.data?.language?.toLocaleLowerCase(),
      {
        style: "long",
        type: "conjunction",
      }
    );

    const items = allowedBrands
      .map((key) => CARD_BRANDS[key]?.label)
      .filter(Boolean);
    return formatter.format(items);
  }, [session.data?.language, allowedBrands]);

  const brandsLabel = formatList();

  const keyboardOpen = useVirtualKeyboardOpen();

  usePreventReload();

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;
  const accentSolid = useSurfaceAccent();
  const onDarkSurface = flavorClassName !== "flavor-a";
  const controlColor = onDarkSurface ? "var(--foreground)" : bodyCopy;
  const fieldColors = {
    background: onDarkSurface ? "var(--background)" : "#fff",
    title: onDarkSurface ? "var(--foreground)" : colorsConfig.title,
    bodyCopy: controlColor,
    inactiveColor: onDarkSurface
      ? "var(--muted-foreground)"
      : colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };

  const handleChange = (field: keyof CardStepFields, value: string) => {
    const next =
      field === "cardHolderName" ? formatCardholderNameInput(value) : value;
    setForm((prev) => ({ ...prev, [field]: next }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): CardFormErrors => {
    const errs: CardFormErrors = {};
    const cleaned = form.cardNumber.replace(/\s/g, "");
    if (!/^\d{13,19}$/.test(cleaned)) {
      errs.cardNumber = t("card.invalidNumber");
    } else {
      const brand = detectCardBrand(form.cardNumber);
      if (!brand || !allowedBrands.includes(brand)) {
        errs.cardNumber = t("card.brandNotAccepted");
      }
    }

    const [mm, yy] = form.expiry.split("/");
    if (!mm || !yy || !/^(0[1-9]|1[0-2])$/.test(mm) || !/^\d{2}$/.test(yy)) {
      errs.expiry = t("card.invalidExpiry");
    } else {
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear() % 100;
      const expMonth = parseInt(mm, 10);
      const expYear = parseInt(yy, 10);
      if (
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth)
      ) {
        errs.expiry = t("card.expiredCard", {
          defaultValue: "Card is expired",
        });
      }
    }

    if (!form.cardHolderName.trim()) {
      errs.cardHolderName = t("card.nameRequired");
    } else if (!isValidCardholderName(form.cardHolderName)) {
      errs.cardHolderName = t("card.nameInvalid");
    }
    if (!/^\d{3,4}$/.test(form.cvv)) errs.cvv = t("card.invalidCvv");

    return errs;
  };

  const handleContinue = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    redirectTo(APP_ROUTES.ADD_CARD_ADDRESS_PAGE, {
      state: { card: form },
    });
  };

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  if (!canSend) {
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    return null;
  }

  return (
    <AuraLayout
      colorConfig={colorsConfig}
      className={`white-view-background ${flavorClassName}`}
    >
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={isUSOnly ? t("card.addUS") : t("card.add")}
          onBackClick={leaveCardForm}
          icon={<CloseLight size={36} colors={[accentSolid, accentSolid]} />}
          onIconClick={leaveCardForm}
        />
      </Layout.Header>

      <Layout.Content>
        {isFlavorB && (
          <section className="cardpage-title-container">
            <section className="cardpage-title">
              <DebitCardIcon size={32} />
              <h2 className="foreground">
                {isUSOnly ? t("card.addDetailsUS") : t("card.addDetails")}
              </h2>
            </section>
            <p className="muted-foreground text-normal">
              {t("card.acceptedBrands", { brands: brandsLabel })}
            </p>
          </section>
        )}
        <div className="cardpage-content">
          <div>
            <FloatingField
              label={t("card.owner")}
              value={form.cardHolderName}
              required
              isValid={!errors.cardHolderName}
              colors={fieldColors}
            >
              <input
                type="text"
                inputMode="text"
                style={{ color: controlColor }}
                value={form.cardHolderName}
                onChange={(e) => handleChange("cardHolderName", e.target.value)}
                maxLength={CARDHOLDER_NAME_INPUT_MAX_LENGTH}
                autoComplete="cc-name"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
              />
            </FloatingField>
            {errors.cardHolderName && (
              <div style={errorStyle}>{errors.cardHolderName}</div>
            )}
          </div>

          <div>
            <FloatingField
              label={t("card.number")}
              placeholder={t("card.numberPlaceholder")}
              value={form.cardNumber}
              required
              isValid={!errors.cardNumber}
              colors={fieldColors}
              adornment={
                detectedBrandInfo ? (
                  <img
                    className="mock-card-brand"
                    src={detectedBrandInfo.logo}
                    alt={t(detectedBrandInfo.altKey)}
                  />
                ) : undefined
              }
            >
              <IMaskInput
                mask="0000 0000 0000 0000"
                style={{ color: controlColor, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                value={form.cardNumber}
                unmask={false}
                onAccept={(value: string) => handleChange("cardNumber", value)}
                inputMode="numeric"
              />
            </FloatingField>
            {errors.cardNumber && (
              <div style={errorStyle}>{errors.cardNumber}</div>
            )}
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <div style={{ flex: 1 }}>
              <FloatingField
                label={t("card.expiry")}
                placeholder={t("card.expiryPlaceholder")}
                value={form.expiry}
                required
                isValid={!errors.expiry}
                colors={fieldColors}
              >
                <IMaskInput
                  mask="MM{/}YY"
                  blocks={{
                    MM: {
                      mask: IMask.MaskedRange,
                      from: 1,
                      to: 12,
                      maxLength: 2,
                    },
                    YY: {
                      mask: IMask.MaskedRange,
                      from: 0,
                      to: 99,
                      maxLength: 2,
                    },
                  }}
                  style={{ color: controlColor, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                  value={form.expiry}
                  onAccept={(value: string) => handleChange("expiry", value)}
                  inputMode="numeric"
                />
              </FloatingField>
              {errors.expiry && <div style={errorStyle}>{errors.expiry}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <FloatingField
                label={t("card.cvvPlaceholder")}
                placeholder={t("card.cvvFormatPlaceholder")}
                value={form.cvv}
                required
                isValid={!errors.cvv}
                colors={fieldColors}
              >
                <IMaskInput
                  mask="000[0]"
                  type="password"
                  style={{ color: controlColor, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                  value={form.cvv}
                  unmask
                  onAccept={(value: string) => handleChange("cvv", value)}
                  inputMode="numeric"
                  autoComplete="cc-csc"
                />
              </FloatingField>
              {errors.cvv && <div style={errorStyle}>{errors.cvv}</div>}
            </div>
          </div>
        </div>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <section
            className="valid-cards"
            style={{
              marginTop: "auto",
              position: "static",
            }}
          >
            {!keyboardOpen && (
              <section className="valid-cards-container">
                <p className="footer-text muted-foreground">
                  {t("card.acceptedBrands", { brands: brandsLabel })}
                </p>
                <section className="valid-cards-logos">
                  {allowedBrands.map((key) => {
                    const brand = CARD_BRANDS[key];
                    if (!brand) return null;
                    return (
                      <img
                        key={key}
                        className="card-logos"
                        src={brand.logo}
                        alt={t(brand.altKey)}
                      />
                    );
                  })}
                </section>
                <p className="footer-text muted-foreground">
                  {t("card.disclaimer")}
                </p>
              </section>
            )}
          </section>
          <Button
            size="small"
            color={textColorBtn}
            background={backgroundBtn}
            onPointerDown={blurActiveTextInput}
            onClick={handleContinue}
          >
            {t("card.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
